import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Tag, Eye, CreditCard, Gift, Sparkles } from 'lucide-react';
import { useAuth, API } from '../App';
import { invalidatePricingConfigCache, SUPPORTED_LOCALES, pickLocalised } from '../hooks/usePricingConfig';

// Iter 209 — Localised text fields can be edited per-language via tabs.
const LOCALISED_KEYS = new Set([
  'foundingTitle', 'foundingTagline', 'foundingIncludesLine',
  'foundingFuturePricingNotice', 'trialDescription', 'affiliateExample',
]);
const LOCALE_LABELS = {
  en: 'English', es: 'Español', nl: 'Nederlands',
  de: 'Deutsch', fr: 'Français', ar: 'العربية',
};

/**
 * AdminPricingConfig — Iter 208.
 *
 * Single-screen editor for every customer-visible pricing string. Loads
 * the current config from `GET /api/pricing-config`, lets the admin tweak
 * any field, then saves via `PUT /api/admin/pricing-config`. After a
 * successful save we invalidate the shared client cache so every other
 * tab/page pulls the new copy on its next render.
 *
 * No Stripe interaction — that still lives in /admin/stripe-rollout.
 * This page is purely copy + mode switching.
 */
const FIELDS = [
  {
    key: 'currentMode', label: 'Current mode', kind: 'select',
    options: [
      { value: 'founding', label: 'Founding (FREE for 12 months messaging)' },
      { value: 'paid', label: 'Paid plans (re-enable billing copy)' },
    ],
    help: 'Switch between Founding-period messaging and paid-plan messaging across the app.',
  },
  { key: 'foundingTitle', label: 'Founding title', kind: 'text', help: 'Shown as the main heading on business guide & Sign-up hero.' },
  { key: 'foundingTagline', label: 'Founding tagline', kind: 'text', help: 'Short benefit line under the title.' },
  { key: 'foundingFreePeriodMonths', label: 'Free period (months)', kind: 'number', help: 'Used in copy like "FREE for {N} months".' },
  { key: 'foundingNoCardRequired', label: 'No card required during free period', kind: 'bool' },
  { key: 'foundingIncludesLine', label: 'What\'s included', kind: 'textarea', help: 'Bullet shown in the Business Guide.' },
  { key: 'foundingFuturePricingNotice', label: 'Future-pricing notice', kind: 'textarea', help: 'Reassures businesses we\'ll communicate before billing returns.' },
  { key: 'currency', label: 'Currency code', kind: 'text', help: 'ISO 4217 — e.g. EUR, GBP, USD.' },
  { key: 'vatIncluded', label: 'Prices include VAT', kind: 'bool' },
  { key: 'trialEnabled', label: 'Show free-trial copy', kind: 'bool' },
  { key: 'trialLengthDays', label: 'Trial length (days)', kind: 'number' },
  { key: 'trialDescription', label: 'Trial description', kind: 'textarea' },
  { key: 'affiliateExample', label: 'Affiliate-program example text', kind: 'textarea' },
];

const AdminPricingConfig = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  // Iter 209 — Per-field editing locale (tabs above each localised input)
  // and preview-pane locale (independent so admin can preview ES while
  // editing EN).
  const [editLang, setEditLang] = useState('en');
  const [previewLang, setPreviewLang] = useState('en');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/pricing-config`);
        if (alive) {
          setForm(r.data);
          setLastSaved(r.data?.updatedAt || null);
        }
      } catch (err) {
        toast.error('Could not load pricing config');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...(prev || {}), [key]: value }));

  // Iter 209 — Localised field setter. Upgrades a legacy string value into
  // a per-locale dict the first time the admin types in another language,
  // preserving the original English text.
  const setLocalisedField = (key, locale, value) => {
    setForm((prev) => {
      const current = (prev || {})[key];
      let map = {};
      if (typeof current === 'string') {
        map = { en: current };
      } else if (current && typeof current === 'object') {
        map = { ...current };
      }
      map[locale] = value;
      return { ...(prev || {}), [key]: map };
    });
  };

  const readLocalised = (key, locale) => {
    const value = (form || {})[key];
    if (typeof value === 'string') return locale === 'en' ? value : '';
    if (value && typeof value === 'object') return value[locale] || '';
    return '';
  };

  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => { if (form[f.key] !== undefined) payload[f.key] = form[f.key]; });
      const r = await axios.put(`${API}/admin/pricing-config`, payload, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setForm(r.data.config);
      setLastSaved(r.data.config?.updatedAt || null);
      invalidatePricingConfigCache();
      toast.success('Pricing config saved. New copy is live for all users.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <header className="bg-slate-900 text-white px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/admin/dashboard" className="p-1 rounded hover:bg-white/10" data-testid="admin-pricing-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-base font-bold flex items-center gap-2"><Tag className="h-4 w-4" /> Pricing Configuration</h1>
            <p className="text-xs text-slate-300">Edit every customer-visible pricing string in one place.</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6" data-testid="admin-pricing-config-form">
        {loading || !form ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Edit form (left) */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
              {FIELDS.map((f) => (
                <div key={f.key} data-testid={`pricing-field-${f.key}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <label className="block text-sm font-semibold text-slate-900" htmlFor={`pf-${f.key}`}>
                      {f.label}
                      {LOCALISED_KEYS.has(f.key) && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 font-bold">multi-language</span>
                      )}
                    </label>
                  </div>
                  {LOCALISED_KEYS.has(f.key) && (
                    <div className="flex flex-wrap gap-1 mb-2" data-testid={`lang-tabs-${f.key}`}>
                      {SUPPORTED_LOCALES.map(loc => (
                        <button
                          key={loc}
                          type="button"
                          onClick={() => setEditLang(loc)}
                          className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                            editLang === loc
                              ? 'bg-amber-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`lang-tab-${f.key}-${loc}`}
                        >
                          {LOCALE_LABELS[loc]}
                        </button>
                      ))}
                    </div>
                  )}
                  {f.kind === 'text' && (
                    LOCALISED_KEYS.has(f.key) ? (
                      <input id={`pf-${f.key}`} type="text"
                        value={readLocalised(f.key, editLang)}
                        onChange={(e) => setLocalisedField(f.key, editLang, e.target.value)}
                        placeholder={editLang === 'en' ? '' : `${LOCALE_LABELS[editLang]} translation (falls back to EN if blank)`}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" maxLength={600} />
                    ) : (
                      <input id={`pf-${f.key}`} type="text" value={form[f.key] ?? ''}
                        onChange={(e) => setField(f.key, e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" maxLength={600} />
                    )
                  )}
                  {f.kind === 'textarea' && (
                    LOCALISED_KEYS.has(f.key) ? (
                      <textarea id={`pf-${f.key}`}
                        value={readLocalised(f.key, editLang)}
                        onChange={(e) => setLocalisedField(f.key, editLang, e.target.value)}
                        rows={3}
                        placeholder={editLang === 'en' ? '' : `${LOCALE_LABELS[editLang]} translation (falls back to EN if blank)`}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" maxLength={600} />
                    ) : (
                      <textarea id={`pf-${f.key}`} value={form[f.key] ?? ''}
                        onChange={(e) => setField(f.key, e.target.value)} rows={3}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" maxLength={600} />
                    )
                  )}
                  {f.kind === 'number' && (
                    <input id={`pf-${f.key}`} type="number" value={form[f.key] ?? 0}
                      min={0} max={60}
                      onChange={(e) => setField(f.key, parseInt(e.target.value || '0', 10))}
                      className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                  )}
                  {f.kind === 'bool' && (
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!form[f.key]}
                        onChange={(e) => setField(f.key, e.target.checked)} />
                      <span>{form[f.key] ? 'Enabled' : 'Disabled'}</span>
                    </label>
                  )}
                  {f.kind === 'select' && (
                    <select id={`pf-${f.key}`} value={form[f.key] ?? f.options[0].value}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                      {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                  {f.help && <p className="text-[11px] text-slate-500 mt-1">{f.help}</p>}
                </div>
              ))}
            </div>

            {/* Live preview (right) */}
            <div>
              <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid="preview-lang-selector">
                <span className="text-xs font-semibold text-slate-600">Preview language:</span>
                {SUPPORTED_LOCALES.map(loc => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => setPreviewLang(loc)}
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                      previewLang === loc
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`preview-lang-tab-${loc}`}
                  >
                    {LOCALE_LABELS[loc]}
                  </button>
                ))}
              </div>
              <PricingLivePreview cfg={form} lang={previewLang} />
            </div>
          </div>
        )}

        {form && !loading && (
          <div className="sticky bottom-4 mt-5 flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3 shadow-md">
            <div className="text-xs text-slate-500">
              {lastSaved
                ? <>Last saved <strong>{new Date(lastSaved).toLocaleString()}</strong></>
                : 'Not yet saved'}
            </div>
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-60"
              data-testid="admin-pricing-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPricingConfig;

/**
 * PricingLivePreview — Iter 208b.
 *
 * Right-pane preview that re-renders on every keystroke in the form so the
 * admin can SEE how the strings land in three customer surfaces before
 * clicking Save:
 *   1. Business Guide (Subscriptions + Billing block in the dashboard).
 *   2. Business Sign-up page hero.
 *   3. Affiliate Program example sentence.
 *
 * Read-only — no buttons fire, no navigation. The visual styling mirrors the
 * live components so the preview is faithful, not approximate.
 */
const PricingLivePreview = ({ cfg, lang = 'en' }) => {
  if (!cfg) return null;
  // Iter 209 — Use pickLocalised so the preview reflects whichever language
  // the admin has selected, even if some locales haven't been filled in yet.
  const L = (key) => pickLocalised(cfg[key], lang);
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4" data-testid="pricing-live-preview">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-500">
        <Eye className="h-4 w-4" /> Live preview
      </div>

      {/* 1. Business Guide */}
      <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="preview-business-guide">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Business Guide</p>
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-2">
          <CreditCard className="h-4 w-4 text-purple-500" /> Subscriptions
        </h4>
        <ul className="text-sm text-gray-600 space-y-1.5 ml-6 list-disc">
          <li>{L('foundingTitle') || '—'}</li>
          <li>{L('foundingTagline') || '—'}</li>
          {cfg.foundingNoCardRequired && <li>No card required.</li>}
          <li>{L('foundingIncludesLine') || '—'}</li>
          <li>Your listing goes live once approved.</li>
        </ul>
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mt-3 mb-2">
          <Tag className="h-4 w-4 text-purple-500" /> Billing &amp; Payments
        </h4>
        <ul className="text-sm text-gray-600 space-y-1.5 ml-6 list-disc">
          <li>
            {cfg.currentMode === 'founding'
              ? `No payment is required during the ${cfg.foundingFreePeriodMonths || 12}-month founding member period.`
              : 'Subscriptions are processed via Stripe Checkout.'}
          </li>
          <li>{L('foundingFuturePricingNotice') || '—'}</li>
          {cfg.trialEnabled && L('trialDescription') && (
            <li className="text-emerald-700"><Sparkles className="inline h-3 w-3 mr-1" />{L('trialDescription')}</li>
          )}
        </ul>
      </div>

      {/* 2. Business Sign-up hero */}
      <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4" data-testid="preview-signup-hero">
        <p className="text-[10px] uppercase tracking-wider text-purple-500 font-bold mb-2">Business Sign-up Hero</p>
        <p className="text-sm text-purple-900 leading-relaxed">
          ✨ <strong>{L('foundingTitle') || '—'}.</strong> {L('foundingTagline') || '—'}
        </p>
      </div>

      {/* 3. Affiliate example */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="preview-affiliate">
        <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-2 flex items-center gap-1">
          <Gift className="h-3 w-3" /> Affiliate Program Example
        </p>
        <p className="text-sm text-amber-900 leading-relaxed">
          <strong>Example:</strong> {L('affiliateExample') || '—'}
        </p>
      </div>

      <p className="text-[11px] text-slate-400 text-center italic">
        Read-only preview. Changes only persist after you click <strong>Save changes</strong>.
      </p>
    </div>
  );
};
